import { Link } from 'react-router-dom';
import { RotateCcw, Trophy } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DiretoriaResultCard } from './components/DiretoriaResultCard';

const formatarData = (valor: string | Date) =>
  new Date(valor).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

export function AptidaoResultadoPage() {
  const ultimo = trpc.aptidao.meuUltimoResultado.useQuery();
  const historico = trpc.aptidao.minhasTentativas.useQuery();

  if (ultimo.isLoading) {
    return <div className="text-gray-500">Carregando seu resultado…</div>;
  }

  if (ultimo.isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        Não foi possível carregar seu resultado: {ultimo.error.message}
      </div>
    );
  }

  if (!ultimo.data) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardContent className="space-y-4 py-8 text-center">
            <p className="text-gray-600">Você ainda não respondeu ao teste de aptidão.</p>
            <Link to="/aptidao">
              <Button>Fazer o teste agora</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { ranking } = ultimo.data.resultado;
  const top3 = ranking.slice(0, 3);
  const tentativasAnteriores = (historico.data ?? []).filter((t) => t.id !== ultimo.data!.id);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Trophy className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Seu resultado</h1>
        </div>
        <p className="text-sm text-gray-600">
          Respondido em {formatarData(ultimo.data.createdAt)} - estas são as diretorias com as
          quais você mais combina.
        </p>
      </header>

      <div className="space-y-3">
        {top3.map((item, indice) => (
          <DiretoriaResultCard
            key={item.diretoriaId}
            posicao={indice + 1}
            nome={item.nome}
            emoji={item.emoji}
            descricao={item.descricao}
            pontos={item.pontos}
            percentual={item.percentual}
            destaque={indice === 0}
          />
        ))}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
          <p className="text-sm text-gray-600">
            O resultado é um ponto de partida para a conversa, não uma sentença. Quer testar
            outras respostas?
          </p>
          <Link to="/aptidao">
            <Button variant="outline">
              <RotateCcw className="h-4 w-4" />
              Refazer o teste
            </Button>
          </Link>
        </CardContent>
      </Card>

      {tentativasAnteriores.length > 0 && (
        <Card>
          <CardContent className="space-y-2 py-4">
            <h2 className="text-sm font-semibold text-gray-700">Tentativas anteriores</h2>
            <ul className="divide-y divide-gray-100">
              {tentativasAnteriores.map((tentativa) => {
                const topo = tentativa.resultado.ranking[0];
                return (
                  <li
                    key={tentativa.id}
                    className="flex items-center justify-between gap-3 py-2 text-sm"
                  >
                    <span className="text-gray-500">{formatarData(tentativa.createdAt)}</span>
                    <span className="text-gray-800">
                      {topo?.emoji} {topo?.nome}
                    </span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
